import * as _ from "lodash";
import * as hash from 'object-hash';
import { MutableRefObject, useContext, useEffect, useReducer, useState } from 'react';
import { Dispatch, connect } from 'react-redux';
import { ICenter, IRecipientFinancialInfo, IRecipientFinancialInfoPost } from '@nkr/models/index';
import { InsuranceGroup } from '@nkr/enums/index';
import { AppActionCreators, IApplicationState, IRecipientFinancialInfoPostResponse, IRecipientFinancialInfoResponse, RecipientActionCreators } from '@nkr/store/index';
import { AfterAction, RecipientProfileContext } from '../models';
import { financialReducer, initialState } from './state';
import { toast } from 'react-toastify';
import { ProfileWrapper } from '../styles';
import Content from "./content";
import { ILookupData } from '../lookup-data';
import DuplicateRecordModal from '@nkr/components/duplicate-record-modal';

interface IProps {
    saveRef: MutableRefObject<any>,
    lookupData: ILookupData
}

interface IStateProps {
    center: ICenter,
    groupIsSuper: boolean
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getFinancialInfo: (centerId: number, recipientId: number) => Promise<IRecipientFinancialInfoResponse>,
    updateFinancialInfo: (centerId: number, recipientId: number, data: IRecipientFinancialInfoPost) => Promise<IRecipientFinancialInfoPostResponse>
}

type TProps = IStateProps & IDispatchProps & IProps;

const FinancialInformation = (props: TProps) => {
    const context = useContext(RecipientProfileContext);
    const [state, dispatch] = useReducer(financialReducer, initialState);
    const [showDuplicateRecord, setShowDuplicateRecord] = useState(false);
    const [duplicateMessage, setDuplicateMessage] = useState<string>('');

    useEffect(() => {
        getData();
    }, []);

    const handleDuplicateError = (error: any): boolean => {
        if (error.response.status != 409) {
            return false;
        }

        setDuplicateMessage(error.response.data);
        setShowDuplicateRecord(true);

        return true;
    };

    const handleCloseDuplicateRecord = () => {
        setShowDuplicateRecord(false);
    };

    const getData = () => {
        props.showLoading('Getting financial info, one moment please...').then(() => {
            props.getFinancialInfo(context.centerId, context.recipientId)
                .then(response => {
                    if (response.data.success) {
                        let data = response.data.results;
                        let { isPrimaryInsuranceLocked, isSecondaryInsuranceLocked } = data;

                        dispatch({
                            type: 'afterLoad',
                            value: {
                                data,
                                hash: hash(data),
                                isPrimaryInsuranceLocked: isPrimaryInsuranceLocked && !props.groupIsSuper,
                                isSecondaryInsuranceLocked: isSecondaryInsuranceLocked && !props.groupIsSuper
                            }
                        });

                        checkAlert(data);
                        toggleInsurance(data);
                    }
                }).finally(() => {
                    props.hideLoading();
                })
        })
    };

    const isMedicareAB = (group: number) => {
        let list = [InsuranceGroup.Medicare, InsuranceGroup.MedicareAdvantage];
        return list.indexOf(group) >= 0;
    };

    const hasMedicareAB = (primaryGroup: number, secondaryGroup: number) => {
        return isMedicareAB(primaryGroup) || isMedicareAB(secondaryGroup);
    };

    const isPrivate = (x: number) => {
        let list = [InsuranceGroup.Medicaid, InsuranceGroup.Medicare, InsuranceGroup.MedicareAOnly, InsuranceGroup.MedicareAdvantage, InsuranceGroup.MediCal, InsuranceGroup.Tricare, InsuranceGroup.TriWest];
        return list.indexOf(x) < 0;
    };

    const isInsuranceComplete = (data: IRecipientFinancialInfo) => {
        let { primaryGroup, secondaryGroup, isMedicarePostTransplant, globalCaseRate, privInsGlobalEndDays: privInsGlobalEndDays } = data;

        let isPrimaryValid = true;

        if (primaryGroup == InsuranceGroup.Other) {
            isPrimaryValid = (data.primaryDetails ?? "").trim() != '';
        } else {
            isPrimaryValid = primaryGroup > 0;
        }

        let isSecondaryValid = secondaryGroup != InsuranceGroup.Other || (data.secondaryDetails ?? "").trim() != '';
        let hasMedicare = hasMedicareAB(primaryGroup, secondaryGroup);
        let medicareDataComplete = isMedicarePostTransplant !== null;
        let hasPrivateIns = (primaryGroup > 0 && isPrivate(primaryGroup)) || (secondaryGroup > 0 && isPrivate(secondaryGroup));
        let privateDataCompleted = (globalCaseRate === true && privInsGlobalEndDays !== null) || validatePrivateInsuranceDetails();
        let isComplete = isPrimaryValid && isSecondaryValid && (hasPrivateIns ? privateDataCompleted : true) && (hasMedicare ? medicareDataComplete : true);

        return isComplete;
    };

    const toggleInsurance = (data: IRecipientFinancialInfo) => {
        let { primaryGroup, secondaryGroup } = data;

        let hasPrimary = primaryGroup > 0;
        let hasSecondary = secondaryGroup > 0;

        let isPrimaryMedicare = isMedicareAB(primaryGroup);
        let isSecondaryMedicare = isMedicareAB(secondaryGroup);

        let medicareDetailsNeeded = isPrimaryMedicare || isSecondaryMedicare;
        let privateDetailsNeeded = (hasPrimary && isPrivate(primaryGroup)) || (hasSecondary && isPrivate(secondaryGroup))

        dispatch({
            type: 'toggleInsurance',
            value: {
                medicareDetailsNeeded,
                privateDetailsNeeded
            }
        });
    };

    const checkAlert = (data: IRecipientFinancialInfo) => {
        let allDetailsFilled = isInsuranceComplete(data);

        dispatch({
            type: 'updateAllDetailsFilled',
            value: allDetailsFilled
        });
    };

    const handleSave = () => {
        if (isInsuranceComplete(state.data)) {
            let newData: IRecipientFinancialInfoPost = { ...data }

            props.showLoading('Saving financial info, one moment please...').then(() => {
                props.updateFinancialInfo(context.centerId, context.recipientId, newData).then((res) => {
                    if (res.data.success) {
                        toast.success('Financial info updated successfully');
                        getData();

                        context.updateHasChanges(false);
                    }
                })
                .catch((error: any) => {
                    handleDuplicateError(error);

                    props.hideLoading();
                })
            }).catch((erro: any) => {
                props.hideLoading();
            });
        } else {
            toast.warn('Please complete the indicated fields...');
        }
    };

    const validatePrivateInsuranceDetails = () => {
        let { patientName, policyIdNumber, groupNumber, healthPlanName, claimsMailingAddress, subscriberName, caseManagerName, caseManagerPhone } = state.data

        return patientName !== ''
            && policyIdNumber !== ''
            && groupNumber !== ''
            && healthPlanName !== ''
            && claimsMailingAddress !== ''
            && subscriberName !== ''
            && caseManagerName !== ''
            && caseManagerPhone !== ''
    };

    const handleDataChange = (data: IRecipientFinancialInfo) => {
        let newHash = hash(data);

        var hasChanged = state.hash != newHash;

        dispatch({
            type: 'updateData',
            value: data
        });

        context.updateHasChanges(hasChanged);

        checkAlert(data);
        toggleInsurance(data);
    };

    context.registerSave(props.saveRef, handleSave);

    const { data, allDetailsFilled, medicareDetailsNeeded, privateDetailsNeeded, isPrimaryInsuranceLocked, isSecondaryInsuranceLocked } = state;

    const privateDataCompleted = (data.globalCaseRate === true && data.privInsGlobalEndDays !== null) || validatePrivateInsuranceDetails()

    if (context.headerInfo == null)
        return null;

    return (
        <ProfileWrapper>
            <Content
                data={data}
                allDetailsFilled={allDetailsFilled}
                medicareDetailsNeeded={medicareDetailsNeeded}
                privateDetailsNeeded={privateDetailsNeeded}
                isPrimaryInsuranceLocked={isPrimaryInsuranceLocked}
                isSecondaryInsuranceLocked={isSecondaryInsuranceLocked}
                privateDataCompleted={privateDataCompleted}
                insuranceGroupList={props.lookupData.insuranceGroupList}
                onChange={handleDataChange}
            />

            <DuplicateRecordModal
                show={showDuplicateRecord}
                message={duplicateMessage}
                onClose={handleCloseDuplicateRecord}
            />
        </ProfileWrapper>
    );
}

const mapStateToProps = (appState: IApplicationState, ownProps: IProps): IStateProps => {
    return {
        center: appState.app.center,
        groupIsSuper: appState.app.user.group.isSuper
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getFinancialInfo: (centerId, recipientId) => {
            return dispatch(RecipientActionCreators.getFinancialInfo(centerId, recipientId))
        },
        updateFinancialInfo: (centerId, recipientId, data) => {
            return dispatch(RecipientActionCreators.updateFinancialInfo(centerId, recipientId, data))
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(FinancialInformation);
